import { DEFAULT_ITEM_HEIGHT, NUMBER_OF_ITEMS, SEARCH_BAR_HEIGHT_WITH_PADDING } from './constants';
import { defaultGetOptionSelected } from './defaultRenderers';
import FilterList, { Props as FilterListProps } from './index';
import { BaseProps } from '#components';
import { Select, AutocompleteChangeDetails, AutocompleteChangeReason, Theme, SxProps } from '@mui/material';
import React, { PropsWithChildren, useState } from 'react';

export type Props<TData> = BaseProps &
  FilterListProps<TData> & {
    // Allow custom styling of the Select component
    selectSxProps?: SxProps<Theme>;
    // Use the select in a controlled manner
    open?: boolean;

    disabled?: boolean;
    // If true, only one item can be selected and we will close the menu onChange
    single?: boolean;
    // Function to return innner content of the select.
    getLabel?: () => string | React.ReactNode;
    onOpen?: () => void;
    onClose?: () => void;
    error?: boolean;

    // dataTestId for the select
    selectDataTestId?: string;
    // dataTestId for the filter list
    filterListDataTestId?: string;
  };

const FilterSelect = <TData,>({
  open,
  single = false,
  getLabel,
  onOpen,
  onClose,
  error,
  selectDataTestId,
  filterListDataTestId,
  selectSxProps,
  ...filterListProps
}: PropsWithChildren<Props<TData>>): JSX.Element => {
  // If autoFocus is true, we want to open the filter list immediately
  const [filterOpen, setFilterOpen] = useState(false);
  const { options, onChange, isOptionEqualToValue, ...rest } = filterListProps;

  // If the select is controlled, use the open prop to determine if the menu is open
  const isOpen = open ?? filterOpen;

  const handleChange = (
    event: React.SyntheticEvent,
    values: TData[],
    reason: AutocompleteChangeReason,
    details: AutocompleteChangeDetails<TData> | undefined
  ) => {
    if (single) {
      setFilterOpen(false);
    }
    onChange?.(event, values, reason, details);
  };

  const getListHeight = () => {
    if (options.length > NUMBER_OF_ITEMS) {
      return NUMBER_OF_ITEMS * DEFAULT_ITEM_HEIGHT;
    }
    return options.length * DEFAULT_ITEM_HEIGHT + SEARCH_BAR_HEIGHT_WITH_PADDING;
  };

  return (
    <Select
      // eslint-disable-next-line no-restricted-syntax
      sx={selectSxProps}
      data-testid={selectDataTestId}
      disabled={rest.disabled}
      open={isOpen}
      value=""
      error={error}
      onOpen={() => {
        onOpen?.();
        setFilterOpen(true);
      }}
      onClose={() => {
        onClose?.();
        setFilterOpen(false);
      }}
      displayEmpty
      fullWidth
      renderValue={(value) => {
        if (getLabel) {
          return getLabel();
        }
        return value;
      }}
    >
      <FilterList
        dataTestId={filterListDataTestId}
        options={options}
        autoFocus
        getListHeight={getListHeight}
        onChange={handleChange}
        isOptionEqualToValue={isOptionEqualToValue || defaultGetOptionSelected}
        {...rest}
      />
    </Select>
  );
};

export default FilterSelect;
